import { type ComponentType, useState } from 'react';
import type { TransactionFilters } from './api/types';
import { CurrencyToggle } from './components/CurrencyToggle';
import { ThemeToggle } from './components/ThemeToggle';
import { BankIcon, HomeIcon, type IconProps, ListIcon, TagIcon } from './components/icons';
import { Analytics } from './screens/Analytics';
import { Budget } from './screens/Budget';
import { Categories } from './screens/Categories';
import { Dashboard } from './screens/Dashboard';
import { Debts } from './screens/Debts';
import { Health } from './screens/Health';
import { Limits } from './screens/Limits';
import { Transactions } from './screens/Transactions';

type Tab =
  | 'dashboard'
  | 'debts'
  | 'transactions'
  | 'categories'
  | 'analytics'
  | 'budget'
  | 'limits'
  | 'health';

const TABS: Array<{ id: Tab; label: string; Icon: ComponentType<IconProps> }> = [
  { id: 'dashboard', label: 'Главная', Icon: HomeIcon },
  { id: 'transactions', label: 'История', Icon: ListIcon },
  { id: 'debts', label: 'Долги', Icon: BankIcon },
  { id: 'categories', label: 'Категории', Icon: TagIcon },
];

export function App(): JSX.Element {
  const [tab, setTab] = useState<Tab>('dashboard');
  // Префиксованные фильтры передаются один раз при переходе на Транзакции
  // (например drill-down из Аналитики). После открытия — Transactions сам владеет state.
  const [pendingTxFilters, setPendingTxFilters] = useState<TransactionFilters | null>(null);

  const goTo = (next: Tab, opts?: { txFilters?: TransactionFilters }): void => {
    if (opts?.txFilters) setPendingTxFilters(opts.txFilters);
    setTab(next);
  };

  return (
    <div className="app">
      {/* Две настройки отображения живут одной группой в углу: валюта и тема. */}
      <div className="display-toggles">
        <ThemeToggle />
        <CurrencyToggle />
      </div>
      <main className="content">
        {tab === 'dashboard' ? <Dashboard onNavigate={(t) => goTo(t)} /> : null}
        {tab === 'debts' ? <Debts /> : null}
        {tab === 'transactions' ? (
          <Transactions
            initialFilters={pendingTxFilters}
            onConsumeInitialFilters={() => setPendingTxFilters(null)}
          />
        ) : null}
        {tab === 'categories' ? <Categories /> : null}
        {tab === 'analytics' ? (
          <Analytics
            onBack={() => setTab('dashboard')}
            onDrillDown={(f) => goTo('transactions', { txFilters: f })}
            onNavigateRoot={(t) => goTo(t)}
          />
        ) : null}
        {tab === 'budget' ? <Budget onBack={() => setTab('dashboard')} /> : null}
        {tab === 'limits' ? <Limits onBack={() => setTab('dashboard')} /> : null}
        {tab === 'health' ? <Health onBack={() => setTab('dashboard')} /> : null}
      </main>
      {tab !== 'analytics' && tab !== 'budget' && tab !== 'limits' && tab !== 'health' ? (
        <nav className="tabbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <div className="tab-icon">
                <t.Icon />
              </div>
              <div className="tab-label">{t.label}</div>
            </button>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
